import React, { useState, useEffect } from "react";
import { Trash2, Pencil, Check, X, Loader2 } from "lucide-react";

interface AssetCardProps {
  asset: BrandAsset;
  onDelete: (assetId: string) => void;
  onUpdate: (assetId: string, updates: FormData) => void;
  renderMeta?: (asset: BrandAsset) => React.ReactNode;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, onDelete, onUpdate, renderMeta }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(asset.name);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sync local name when the asset prop changes (e.g. after API response)
  useEffect(() => {
    if (!isEditing) setName(asset.name);
  }, [asset.name, isEditing]);

  const handleSaveName = () => {
    if (name.trim() && name !== asset.name) {
      const fd = new FormData();
      fd.append("name", name.trim());
      onUpdate(asset.id, fd);
    }
    setIsEditing(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(asset.id);
    } catch {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative group bg-gray-900 border border-gray-700 rounded-lg">
      <div className="aspect-square flex items-center justify-center p-3 bg-gray-900/50 overflow-hidden rounded-t-lg">
        <img
          src={asset.thumbnail_url || asset.image_url}
          alt={asset.name}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
        />
      </div>
      <div className="p-3 border-t border-gray-700">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-grow bg-gray-800 border border-gray-600 text-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
            />
            <button onClick={handleSaveName} className="text-green-400 hover:text-green-300 p-1">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={() => { setIsEditing(false); setName(asset.name); }} className="text-gray-400 hover:text-gray-300 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-200 truncate">{asset.name}</span>
            <button onClick={() => setIsEditing(true)} className="text-gray-500 hover:text-gray-300 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Pencil className="w-3 h-3" />
            </button>
          </div>
        )}
        {asset.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {asset.tags.map((tag) => (
              <span key={tag} className="text-xs bg-gray-700 text-gray-300 rounded px-1.5 py-0.5">{tag}</span>
            ))}
          </div>
        )}
        {renderMeta && <div className="mt-2">{renderMeta(asset)}</div>}
      </div>
      <button
        onClick={handleDelete}
        disabled={isDeleting}
        className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
      >
        {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      </button>
    </div>
  );
};

export default AssetCard;
